import React, { useEffect, useState } from 'react';
import { ArrowUp, ArrowDown, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api/axios';

// Display order of shift types (Shift Templates page, admins): shifts of one day
// with the same start time are listed in this order in every roster view.
// Display only — the generator and stored shifts are not affected.

export function moveItem(list, index, delta) {
  const to = index + delta;
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  [next[index], next[to]] = [next[to], next[index]];
  return next;
}

export default function ShiftTypeOrderCard() {
  const [saved, setSaved] = useState(null);
  const [order, setOrder] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/shifts/shift-type-order')
      .then((r) => { setSaved(r.data.order); setOrder(r.data.order); })
      .catch(() => toast.error('Failed to load the shift type order.'));
  }, []);

  if (!order) return null;
  const changed = order.map((t) => t.key).join() !== saved.map((t) => t.key).join();

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put('/shifts/shift-type-order', { order: order.map((t) => t.key) });
      setSaved(r.data.order);
      setOrder(r.data.order);
      toast.success('Display order saved.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save the order.');
    } finally {
      setBusy(false);
    }
  };

  const btn = { minWidth: '40px', minHeight: '40px', justifyContent: 'center', padding: '0 8px' };

  return (
    <div className="card" style={{ padding: '14px 16px', marginBottom: '16px' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 8px', width: '100%', background: 'none', border: 'none', padding: 0, cursor: 'pointer', minHeight: '36px', textAlign: 'left' }}
      >
        <strong style={{ fontSize: '0.95rem' }}>Display order of shift types</strong>
        <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', flex: '1 1 220px', minWidth: 0 }}>
          {saved.map((t) => t.label).join(' · ')}
        </span>
        <ChevronDown size={14} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.18s', flexShrink: 0 }} />
      </button>
      {open && (
        <div style={{ marginTop: '10px' }}>
          <p style={{ margin: '0 0 10px', fontSize: '0.82rem', color: 'var(--text-light)', lineHeight: 1.5 }}>
            Shifts of one day are listed by start time; shifts starting at the same time follow this order (then the
            longer one first). Applies to all roster views and lists. Display only — planning is not affected.
          </p>
          <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '6px', maxWidth: '420px' }}>
            {order.map((t, i) => (
              <li
                key={t.key}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--white)' }}
              >
                <span style={{ width: '20px', textAlign: 'right', color: 'var(--text-muted)', fontSize: '0.8rem' }}>{i + 1}.</span>
                <span style={{ flex: 1, minWidth: 0, fontWeight: 600, fontSize: '0.88rem' }}>{t.label}</span>
                <button type="button" className="btn btn-secondary btn-sm" style={btn} disabled={busy || i === 0}
                  aria-label={`Move ${t.label} up`} onClick={() => setOrder((o) => moveItem(o, i, -1))}>
                  <ArrowUp size={15} />
                </button>
                <button type="button" className="btn btn-secondary btn-sm" style={btn} disabled={busy || i === order.length - 1}
                  aria-label={`Move ${t.label} down`} onClick={() => setOrder((o) => moveItem(o, i, 1))}>
                  <ArrowDown size={15} />
                </button>
              </li>
            ))}
          </ol>
          <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-primary btn-sm" style={{ minHeight: '40px' }} disabled={busy || !changed} onClick={save}>
              {busy ? 'Saving…' : 'Save order'}
            </button>
            {changed && (
              <button type="button" className="btn btn-secondary btn-sm" style={{ minHeight: '40px' }} disabled={busy} onClick={() => setOrder(saved)}>
                Cancel
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
